import { useState, useEffect } from 'react';
import MapComponent from '../components/MapComponent';
import DrawingManager from '../components/DrawingManager';
import PlacesAutocomplete from '../components/PlacesAutocomplete';
import KmzUploader from '../components/KmzUploader';
import EraserTool from '../components/EraserTool';
import { APIProvider, AdvancedMarker, Pin } from '@vis.gl/react-google-maps';
import CustomerPins from '../components/CustomerPins';
import GeocodeHelper from '../components/GeocodeHelper';
import * as XLSX from 'xlsx';
import ProductSettings from '../components/admin/ProductSettings';
import CompanyBrandingSettings from '../components/admin/CompanyBrandingSettings';
import Model3DManager from '../components/admin/Model3DManager';
import EmailSettings from '../components/admin/EmailSettings';
import { Eye } from 'lucide-react';

const API_KEY = "AIzaSyALpDWAlpYMJc5RtcLG31oOvXYVRmwthXw";

export default function AdminPanel() {
  const userStr = localStorage.getItem('user');
  const currentUser = userStr ? JSON.parse(userStr) : null;
  const [activeTab, setActiveTab] = useState('customers');
  const [importedFeatures, setImportedFeatures] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [eraserActive, setEraserActive] = useState(false);
  const [eraserBounds, setEraserBounds] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [teamColors, setTeamColors] = useState({});
  const [teamNames, setTeamNames] = useState({});
  const [newTeamName, setNewTeamName] = useState('');
  const [teamEmails, setTeamEmails] = useState({});
  
  const [usersList, setUsersList] = useState([]);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('sale');
  const [newUserTeam, setNewUserTeam] = useState('');
  const [newUserSalesCode, setNewUserSalesCode] = useState('');
  
  // Customers Management State
  const [customerData, setCustomerData] = useState([]);
  const [existingCustomers, setExistingCustomers] = useState([]);
  const [pendingGeocodes, setPendingGeocodes] = useState([]);
  const [manualGeocodeCustomer, setManualGeocodeCustomer] = useState(null);
  const [manualPinLocation, setManualPinLocation] = useState(null);
  const [showCustomersOnMap, setShowCustomersOnMap] = useState(false);
  
  // Inline Edit State
  const [editingCustomerCdNo, setEditingCustomerCdNo] = useState(null);
  const [editCustomerData, setEditCustomerData] = useState({});
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [showFailedGeocodeOnly, setShowFailedGeocodeOnly] = useState(false);
  
  // Sorting State
  const [importSortConfig, setImportSortConfig] = useState({ key: null, direction: 'asc' });
  const [customerSortConfig, setCustomerSortConfig] = useState({ key: null, direction: 'asc' });

  const handleSort = (key, config, setConfig) => {
    let direction = 'asc';
    if (config.key === key && config.direction === 'asc') {
      direction = 'desc';
    }
    setConfig({ key, direction });
  };

  const getSortedData = (data, config) => {
    if (!config.key) return data;
    return [...data].sort((a, b) => {
      const valA = a[config.key] || '';
      const valB = b[config.key] || '';
      if (typeof valA === 'string' && typeof valB === 'string') {
        return config.direction === 'asc' 
          ? valA.localeCompare(valB, 'th') 
          : valB.localeCompare(valA, 'th');
      }
      if (valA < valB) return config.direction === 'asc' ? -1 : 1;
      if (valA > valB) return config.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };
  const fetchUsers = async () => {
    try {
      const res = await fetch('/api/users');
      const data = await res.json();
      if (data.status === 'success') {
        setUsersList(data.data);
      }
    } catch (err) {
      console.error("Error fetching users", err);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await fetch('/api/customers');
      const data = await res.json();
      if (data.status === 'success') {
        setExistingCustomers(data.data || []);
      }
    } catch (err) {
      console.error("Error fetching customers", err);
    }
  };

  const handleStartAutoGeocode = () => {
    const missing = existingCustomers.filter(c => !c.lat || c.lat === '');
    if (missing.length === 0) {
      alert("All customers already have coordinates!");
      return;
    }
    setPendingGeocodes(missing);
  };

  const handleGeocodeComplete = async (results) => {
    alert(`Auto-Geocoding completed for ${results.length} customers!`);
    setPendingGeocodes([]);
    fetchCustomers();
  };

  const handleManualMapClick = async (e) => {
    if (!manualGeocodeCustomer) return;
    
    const latLng = e.detail?.latLng || e.latLng;
    if (!latLng) return;
    
    const lat = typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat;
    const lng = typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng;
    
    setManualPinLocation({ lat, lng });
  };

  const saveManualPinLocation = async () => {
    if (!manualGeocodeCustomer || !manualPinLocation) return;
    
    try {
      const response = await fetch('/api/customers/update_coords', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([{ cd_no: manualGeocodeCustomer.cd_no, lat: manualPinLocation.lat, lng: manualPinLocation.lng, status: 'manual' }])
      });
      const data = await response.json();
      if (data.status === 'success') {
        alert(`Location saved for ${manualGeocodeCustomer.search_name}`);
        setManualGeocodeCustomer(null);
        setManualPinLocation(null);
        fetchCustomers();
      }
    } catch (err) {
      console.error(err);
      alert("Failed to save location.");
    }
  };

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'customers') {
      fetchCustomers();
    }
  }, [activeTab]);
  
  // Fetch teams and existing areas on load
  useEffect(() => {
    // Fetch Teams
    fetch('/api/teams')
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          setTeams(data.data);
          if (data.data.length > 0) setSelectedTeamId(data.data[0].id);
          
          const names = {};
          const tColors = {};
          const tEmails = {};
          data.data.forEach(t => {
            names[t.id] = t.team_name;
            tEmails[t.id] = t.team_email || '';
            if (t.color) {
              tColors[t.id] = t.color;
              tColors[t.team_name] = t.color;
            }
          });
          setTeamNames(names);
          setTeamEmails(tEmails);
          setTeamColors(prev => ({ ...prev, ...tColors }));
        }
      })
      .catch(err => console.error("Error fetching teams:", err));

    // Fetch Existing Areas
    fetch('/api/areas')
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          const colors = {};
          const dbFeatures = data.data.map(area => {
            const feature = JSON.parse(area.geojson_data);
            feature.properties = feature.properties || {};
            feature.properties.db_id = area.id;
            feature.properties.team_id = area.team_id;
            delete feature.properties.selected; // Prevent areas from loading as editable
            
            if (area.team_color) {
              colors[area.team_id] = area.team_color;
              if (area.team_name) {
                colors[area.team_name] = area.team_color;
              }
            }
            return feature;
          });
          setTeamColors(prev => ({ ...prev, ...colors }));
          setImportedFeatures(prev => [...prev, ...dbFeatures]);
        }
      })
      .catch(err => console.error("Error fetching existing areas:", err))
      .finally(() => setIsLoading(false));
  }, []);
  
  const handlePolygonComplete = (polygonData) => {
    console.log("New polygon drawn:", polygonData);
    // TODO: Save to DB
  };

  const handleKmzUpload = (geojson) => {
    console.log("Imported GeoJSON:", geojson);
    if (geojson.features && geojson.features.length > 0) {
      const polygons = geojson.features.filter(f => 
        f.geometry && (
          f.geometry.type === 'Polygon' || 
          f.geometry.type === 'MultiPolygon' || 
          f.geometry.type === 'GeometryCollection'
        )
      );
      setImportedFeatures(prev => [...prev, ...polygons]);
    }
  };

  // Handle auto-selecting team when a DB area is clicked
  useEffect(() => {
    if (selectedArea) {
      const dbTeamId = selectedArea.getProperty('team_id');
      if (dbTeamId) {
        setSelectedTeamId(dbTeamId);
      }
    }
  }, [selectedArea]);

  const handleSaveArea = async () => {
    if (!selectedArea) return;
    
    selectedArea.toGeoJson(async (geoJsonFeature) => {
      delete geoJsonFeature.properties.selected;
      try {
        const response = await fetch('/api/areas', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            team_id: selectedTeamId,
            province_name: geoJsonFeature.properties?.name || 'Imported KML Area',
            geojson_data: JSON.stringify(geoJsonFeature)
          })
        });
        
        const result = await response.json();
        if (result.status === 'success') {
          // alert('Area saved successfully!');
          selectedArea.setProperty('db_id', result.id);
          selectedArea.setProperty('team_id', selectedTeamId);
          selectedArea.setProperty('selected', false);
          setSelectedArea(null);
        } else {
          alert('Error saving area: ' + result.message);
        }
      } catch (err) {
        console.error(err);
        alert('Network error while saving area.');
      }
    });
  };

  const handleUpdateArea = async () => {
    if (!selectedArea) return;
    
    const dbId = selectedArea.getProperty('db_id');
    if (!dbId) return;

    selectedArea.toGeoJson(async (geoJsonFeature) => {
      delete geoJsonFeature.properties.selected;
      try {
        const response = await fetch('/api/areas/' + dbId, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            team_id: selectedTeamId,
            geojson_data: JSON.stringify(geoJsonFeature)
          })
        });
        
        const result = await response.json();
        if (result.status === 'success') {
          // alert('Area updated successfully!');
          selectedArea.setProperty('team_id', selectedTeamId);
          selectedArea.setProperty('selected', false);
          setSelectedArea(null);
        } else {
          alert('Error updating area: ' + result.message);
        }
      } catch (err) {
        console.error(err);
        alert('Network error while updating area.');
      }
    });
  };

  const handleDeleteArea = async () => {
    if (!selectedArea) return;
    const dbId = selectedArea.getProperty('db_id');
    if (!dbId) return;

    if (!window.confirm('Are you sure you want to permanently delete this area?')) return;

    try {
      const response = await fetch('/api/areas/' + dbId, {
        method: 'DELETE'
      });
      
      const result = await response.json();
      if (result.status === 'success') {
        alert('Area deleted successfully!');
        window.location.reload();
      } else {
        alert('Error deleting area: ' + result.message);
      }
    } catch (err) {
      console.error(err);
      alert('Network error while deleting area.');
    }
  };

  const handleDeleteEraserPoints = () => {
    if (!selectedArea || !eraserBounds) return;
    
    const geometry = selectedArea.getGeometry();
    if (geometry && geometry.getType() === 'Polygon') {
      const rings = geometry.getArray();
      let pointsDeleted = 0;
      
      const newRings = rings.map(ring => {
        const vertices = ring.getArray();
        const newVertices = vertices.filter(v => {
          // If the vertex is inside the eraser bounds, filter it OUT
          const isInside = eraserBounds.contains(v);
          if (isInside) pointsDeleted++;
          return !isInside;
        });
        
        // Ensure ring stays closed if it was closed
        if (newVertices.length > 0) {
          const first = newVertices[0];
          const last = newVertices[newVertices.length - 1];
          if (first.lat() !== last.lat() || first.lng() !== last.lng()) {
            newVertices.push(first);
          }
        }
        
        return newVertices;
      });
      
      if (pointsDeleted > 0) {
        selectedArea.setGeometry(new window.google.maps.Data.Polygon(newRings));
        alert(`Deleted ${pointsDeleted} points!`);
      } else {
        alert("No points found inside the eraser area.");
      }
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    try {
      const response = await fetch('/api/teams', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ team_name: newTeamName })
      });
      const result = await response.json();
      if (result.status === 'success') {
        const newTeam = { id: result.id, team_name: newTeamName, color: '#eb0a1e' };
        setTeams(prev => [...prev, newTeam]);
        setTeamNames(prev => ({ ...prev, [result.id]: newTeamName }));
        setTeamColors(prev => ({ ...prev, [result.id]: '#eb0a1e' }));
        setNewTeamName('');
        alert('Team created successfully!');
      } else {
        alert('Error: ' + result.message);
      }
    } catch (err) {
      console.error(err);
      alert('Network error creating team');
    }
  };

  const handleUpdateTeam = async (teamId) => {
    const color = teamColors[teamId] || '#eb0a1e';
    const name = teamNames[teamId];
    if (!name.trim()) return;

    try {
      const response = await fetch('/api/teams/' + teamId, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: teamId, team_name: name, color, team_email: teamEmails[teamId] || '' })
      });
      const result = await response.json();
      if (result.status === 'success') {
        setTeams(prev => prev.map(t => t.id === teamId ? { ...t, color, team_name: name } : t));
        alert('Team updated successfully!');
      } else {
        alert('Error: ' + result.message);
      }
    } catch (err) {
      console.error(err);
      alert('Network error updating team');
    }
  };

  const handleSaveTeamEmail = async (teamId) => {
    const email = teamEmails[teamId] || '';
    const name  = teamNames[teamId];
    const color = teamColors[teamId] || '#eb0a1e';
    try {
      const response = await fetch('/api/teams/' + teamId, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: teamId, team_name: name, color, team_email: email })
      });
      const result = await response.json();
      if (result.status === 'success') {
        alert('Email saved for ' + name + '!');
      } else {
        alert('Error: ' + result.message);
      }
    } catch (err) {
      console.error(err);
      alert('Network error saving email');
    }
  };

  const handleCreateUser = async () => {
    if (!newUserEmail.trim()) {
      alert("Please enter a Google Email");
      return;
    }
    
    // Auto-append domain if missing
    let email = newUserEmail.trim();
    if (!email.includes('@')) {
      email += '@tmhwst.toyota-industries.com';
    }

    try {
      const response = await fetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          google_email: email,
          role: newUserRole,
          team_id: ['admin', 'super_admin'].includes(newUserRole) ? null : newUserTeam,
          sales_code: newUserSalesCode
        })
      });
      const result = await response.json();
      if (result.status === 'success') {
        setNewUserEmail('');
        fetchUsers();
        alert('User created successfully!');
      } else {
        alert('Error: ' + result.message);
      }
    } catch (err) {
      console.error(err);
      alert('Network error creating user');
    }
  };

  const handleUpdateUser = async (user) => {
    const email = (user.google_email || '').trim();
    if (!email) {
      alert('Google Email cannot be empty');
      return;
    }

    try {
      const response = await fetch('/api/users/' + user.id, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: user.id,
          google_email: email,
          role: user.role,
          team_id: ['admin', 'super_admin'].includes(user.role) ? null : user.team_id,
          sales_code: user.sales_code
        })
      });
      const result = await response.json();
      if (result.status === 'success') {
        fetchUsers();
        alert('User updated successfully!');
      } else {
        alert('Error: ' + result.message);
      }
    } catch (err) {
      console.error(err);
      alert('Network error updating user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      const response = await fetch('/api/users/' + userId, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      if (result.status === 'success') {
        fetchUsers();
      } else {
        alert('Error: ' + result.message);
      }
    } catch (err) {
      console.error(err);
      alert('Network error deleting user');
    }
  };

  const handleImpersonateUser = (targetUser) => {
    if (!targetUser) return;
    const roleText = targetUser.role === 'super_admin' ? 'Super Admin' :
      targetUser.role === 'admin' ? 'Admin' :
      targetUser.role === 'sale_manager' ? 'Sale Manager' : 'Sale';
    
    const confirmMsg = `คุณต้องการจำลองมุมมองเป็น "${targetUser.google_email}" (${roleText}) หรือไม่?\n\n- ระบบจะแสดงหน้าจอ แผนที่ ลูกค้า และสิทธิ์การใช้งานต่างๆ เสมือนผู้ใช้นี้เข้าสู่ระบบ\n- จะมีแถบสีส้มด้านบนแจ้งเตือนเสมอ และสามารถกด "กลับสู่บัญชี Admin ปกติ" หรือสลับเป็นคนอื่นได้ทันที`;
    if (!window.confirm(confirmMsg)) return;

    // Preserve original admin session if not already stored
    const currentAdminStr = localStorage.getItem('impersonator_admin');
    if (!currentAdminStr && currentUser) {
      localStorage.setItem('impersonator_admin', JSON.stringify(currentUser));
    }

    const targetTeamName = teams.find(t => String(t.id) === String(targetUser.team_id))?.team_name || targetUser.team_name || '';

    const impersonatedData = {
      id: targetUser.id,
      google_email: targetUser.google_email,
      role: targetUser.role,
      team_id: targetUser.team_id,
      team_name: targetTeamName,
      sales_code: targetUser.sales_code || '',
      name: targetUser.google_email ? targetUser.google_email.split('@')[0] : 'User'
    };

    localStorage.setItem('user', JSON.stringify(impersonatedData));

    // Log the start of impersonation
    try {
      fetch('/api/log_activity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: currentUser?.google_email,
          action_type: 'IMPERSONATE_START',
          details: `Admin ${currentUser?.google_email} started impersonating user ${targetUser.google_email} (${targetUser.role})`
        })
      }).catch(e => {});
    } catch(e) {}

    // Navigate to home dashboard so admin immediately sees user's territory/pins
    window.location.href = '/';
  };

  const handlePasteExcel = (e) => {
    const pasteData = e.target.value;
    if (!pasteData) {
      setCustomerData([]);
      return;
    }

    const rows = pasteData.split('\n').filter(row => row.trim() !== '');
    if (rows.length < 2) return; // Need at least header and one data row

    // Parse Headers dynamically to allow any column order
    const headers = rows[0].split('\t').map(h => h.trim().toLowerCase().replace(/[^a-z0-9]/g, ''));
    
    const getIndex = (possibleNames) => {
      for (const name of possibleNames) {
        const cleanName = name.toLowerCase().replace(/[^a-z0-9]/g, '');
        // Exact match of alphanumeric representation
        const idx = headers.findIndex(h => h === cleanName);
        if (idx !== -1) return idx;
      }
      return -1;
    };

    const idxCdNo = getIndex(['cdno']);
    const idxGroup = getIndex(['customergroup', 'group']);
    const idxSearchName = getIndex(['searchname']);
    const idxCustomerName = getIndex(['name', 'customername']);
    const idxSalesTeam = getIndex(['salesteam']);
    const idxUpdateSalesCode = getIndex(['salescode', 'updatesalescode']);
    const idxUpdateAfterSalesCode = getIndex(['aftersalescode', 'updateaftersalescode']);
    const idxAddress = getIndex(['address']);
    const idxCity = getIndex(['city']);

    const parsedData = [];
    for (let i = 1; i < rows.length; i++) {
      const cols = rows[i].split('\t');
      if (cols.length < 2) continue; // Skip empty rows

      // Safety check to grab values based on dynamically found indices
      parsedData.push({
        CD_No: idxCdNo !== -1 && cols[idxCdNo] ? cols[idxCdNo].trim() : '',
        Customer_Group: idxGroup !== -1 && cols[idxGroup] ? cols[idxGroup].trim() : '',
        Search_Name: idxSearchName !== -1 && cols[idxSearchName] ? cols[idxSearchName].trim() : '',
        Customer_Name: idxCustomerName !== -1 && cols[idxCustomerName] ? cols[idxCustomerName].trim() : (idxSearchName !== -1 && cols[idxSearchName] ? cols[idxSearchName].trim() : ''),
        Sales_Team: idxSalesTeam !== -1 && cols[idxSalesTeam] ? cols[idxSalesTeam].trim() : '',
        Update_Sales_Code: idxUpdateSalesCode !== -1 && cols[idxUpdateSalesCode] ? cols[idxUpdateSalesCode].trim() : '',
        Update_After_Sales_Code: idxUpdateAfterSalesCode !== -1 && cols[idxUpdateAfterSalesCode] ? cols[idxUpdateAfterSalesCode].trim() : '',
        Address: idxAddress !== -1 && cols[idxAddress] ? cols[idxAddress].trim() : '',
        City: idxCity !== -1 && cols[idxCity] ? cols[idxCity].trim() : ''
      });
    }
    
    setCustomerData(parsedData);
  };

  const handleSaveCustomers = async () => {
    if (customerData.length === 0) return;
    
    try {
      const response = await fetch('/api/customers/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customerData)
      });
      const result = await response.json();
      if (result.status === 'success') {
        alert(`${result.message}\n\n🟢 เพิ่มข้อมูลใหม่: ${result.inserted} ราย\n🟡 อัปเดตข้อมูลเดิม: ${result.updated} ราย`);
        setCustomerData([]);
        document.getElementById('excel-paste-box').value = '';
        fetchCustomers();
      } else {
        alert('Error: ' + result.message);
      }
    } catch (err) {
      console.error(err);
      alert('Network error saving customers');
    }
  };

  const handleEditCustomerClick = (customer) => {
    setEditingCustomerCdNo(customer.cd_no);
    setEditCustomerData({
      cd_no: customer.cd_no,
      customer_group: customer.customer_group || '',
      search_name: customer.search_name || '',
      customer_name: customer.customer_name || '',
      address: customer.address || '',
      city: customer.city || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingCustomerCdNo(null);
    setEditCustomerData({});
  };

  const handleSaveEdit = async () => {
    if (!editCustomerData.cd_no) return;
    try {
      const response = await fetch('/api/customers/' + editCustomerData.cd_no, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editCustomerData)
      });
      const result = await response.json();
      if (result.status === 'success') {
        alert('Customer updated successfully');
        setEditingCustomerCdNo(null);
        fetchCustomers();
      } else {
        alert('Error: ' + result.message);
      }
    } catch (err) {
      console.error(err);
      alert('Network error updating customer');
    }
  };

  const handleExportExcel = () => {
    if (existingCustomers.length === 0) {
      alert("No customers to export.");
      return;
    }
    
    // Create worksheet from data
    const ws = XLSX.utils.json_to_sheet(existingCustomers);
    
    // Create workbook and append sheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Customers");
    
	// Generate file and trigger download
	XLSX.writeFile(wb, "Database_Customers.xlsx");
  };

  const handleDeleteAllLeads = async () => {
    if (!window.confirm("Are you sure you want to delete ALL marketing leads from the local database? (Google Sheets will NOT be affected)")) {
      return;
    }
    try {
      const res = await fetch('/api/leads', { method: 'DELETE' });
      const data = await res.json();
      if (data.status === 'success') {
        alert("All leads deleted successfully. You can sync again to fetch from Google Sheets.");
      } else {
        alert("Failed to delete leads: " + data.message);
      }
    } catch (err) {
      alert("Network error while deleting leads.");
    }
  };

  const handleDeleteAllCustomers = async () => {
    if (existingCustomers.length === 0) {
      alert("No customers to delete.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete ALL customers? This will download a backup Excel file first.")) {
      return;
    }

    // First download the excel
    handleExportExcel();

    // Now delete from database
    try {
      const res = await fetch('/api/customers', {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.status === 'success') {
        alert("All customers deleted successfully.");
        // Refresh the page or fetch customers again
        window.location.reload();
      } else {
        alert("Failed to delete customers: " + data.message);
      }
    } catch (err) {
      console.error(err);
      alert("Network error while deleting customers.");
    }
  };

  const filteredCustomers = existingCustomers.filter(c => {
    if (showFailedGeocodeOnly) {
      if (c.lat && c.geocode_status !== 'failed') return false;
    }
    if (customerSearchQuery) {
      const q = customerSearchQuery.toLowerCase();
      const match = 
        (c.search_name && String(c.search_name).toLowerCase().includes(q)) ||
        (c.customer_name && String(c.customer_name).toLowerCase().includes(q)) ||
        (c.update_sales_code && String(c.update_sales_code).toLowerCase().includes(q)) ||
        (c.update_after_sales_code && String(c.update_after_sales_code).toLowerCase().includes(q)) ||
        (c.city && String(c.city).toLowerCase().includes(q)) ||
        (c.customer_group && String(c.customer_group).toLowerCase().includes(q)) ||
        (c.cd_no && String(c.cd_no).toLowerCase().includes(q));
      if (!match) return false;
    }
    return true;
  });

  const sortedImportData = getSortedData(customerData, importSortConfig);
  const sortedFilteredCustomers = getSortedData(filteredCustomers, customerSortConfig);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: activeTab === 'areas' ? 'hidden' : 'auto' }}>
      <div style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'baseline', gap: '1rem', flexShrink: 0 }}>
        <h2 style={{ margin: 0 }}>Admin Panel</h2>
        <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>Manage teams, users, and draw sales territories.</p>
      </div>

      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '0.5rem', 
        marginBottom: '1rem', 
        overflowX: 'auto', 
        whiteSpace: 'nowrap',
        flexShrink: 0,
        minHeight: '42px',
        paddingBottom: '2px'
      }}>
        {[
          { id: 'customers', label: 'Database Data' },
          { id: 'users', label: 'Manage Users' },
          { id: 'teamemails', label: '📧 Team Emails' },
          { id: 'email_settings', label: '⚙️ ตั้งค่าอีเมล (SMTP)' },
          { id: 'areas', label: 'Draw Areas' },
          { id: 'products', label: 'Product Settings' },
          { id: 'branding', label: '🏢 โลโก้องค์กร (Branding)' },
          { id: 'models3d', label: '📦 จัดการโมเดล 3D' }
        ].map(tab => (
          <button
            key={tab.id}
            className={`btn ${activeTab === tab.id ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '0.45rem 0.85rem',
              fontSize: '0.875rem',
              fontWeight: activeTab === tab.id ? 600 : 500,
              lineHeight: 1.25,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              height: '38px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            {tab.label}
          </button>
        ))}
        {activeTab === 'areas' && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.75rem', alignItems: 'center', flexShrink: 0 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.875rem', color: '#475569', fontWeight: 600, whiteSpace: 'nowrap' }}>
              <input 
                type="checkbox" 
                checked={showCustomersOnMap} 
                onChange={(e) => setShowCustomersOnMap(e.target.checked)} 
                style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
              />
              Show Customer Pins
            </label>
            <KmzUploader onUploadSuccess={handleKmzUpload} />
          </div>
        )}
      </div>
      
      {activeTab === 'areas' && (
        <div style={{ flex: 1, minHeight: '500px', display: 'flex', gap: '1.5rem' }}>
          <div style={{ flex: 2, position: 'relative' }}>
            {isLoading && (
              <div style={{ position: 'absolute', inset: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 'var(--radius-lg)' }}>
                <div style={{ padding: '1rem 2rem', background: 'white', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', fontWeight: 600, color: 'var(--color-primary)' }}>
                  ⏳ Loading map data...
                </div>
              </div>
            )}
             <MapComponent 
               importedFeatures={importedFeatures} 
               onFeatureSelect={setSelectedArea} 
               editable={true} 
               teamColors={teamColors}
               onClick={handleManualMapClick}
               isPinningMode={!!manualGeocodeCustomer}
             >
                <DrawingManager onPolygonComplete={handlePolygonComplete} />
                <EraserTool active={eraserActive} onBoundsChanged={setEraserBounds} />
                {showCustomersOnMap && <CustomerPins customers={existingCustomers} teamColors={teamColors} />}
                {manualPinLocation && (
                  <AdvancedMarker position={manualPinLocation} zIndex={1000}>
                    <Pin background={'#ef4444'} borderColor={'#7f1d1d'} glyphColor={'#ffffff'} />
                  </AdvancedMarker>
                )}
             </MapComponent>
          </div>
          <div className="card" style={{ flex: 1 }}>
            {manualGeocodeCustomer ? (
              <div style={{ padding: '1rem', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px' }}>
                <h3 style={{ color: '#d97706', marginBottom: '0.5rem' }}>📍 Manual Pinning Mode</h3>
                <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
                  Please click anywhere on the map to drop a pin for:<br/>
                  <strong>{manualGeocodeCustomer.search_name}</strong><br/>
                  <small>{manualGeocodeCustomer.address}</small>
                </p>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn" style={{ flex: 1, backgroundColor: '#f3f4f6', color: '#374151' }} onClick={() => { setManualGeocodeCustomer(null); setManualPinLocation(null); }}>Cancel</button>
                  <button className="btn btn-primary" style={{ flex: 2 }} onClick={saveManualPinLocation} disabled={!manualPinLocation}>Save Location</button>
                </div>
              </div>
            ) : (
              <>
                <h3>Area Details</h3>
            <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>Select or draw an area on the map to assign it to a team.</p>
            <div style={{ marginTop: '1.5rem' }}>
               <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Assign to Team</label>
               <select 
                 className="input-field" 
                 value={selectedTeamId} 
                 onChange={(e) => setSelectedTeamId(e.target.value)}
               >
                 {teams.map(team => (
                   <option key={team.id} value={team.id}>{team.team_name}</option>
                 ))}
               </select>
               {selectedArea && selectedArea.getProperty('db_id') ? (
                 <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                   <button 
                     className="btn btn-primary" 
                     style={{ flex: 2 }}
                     onClick={handleUpdateArea}
                   >
                     Update Area
                   </button>
                   <button 
                     className="btn" 
                     style={{ flex: 1, backgroundColor: '#ef4444', color: 'white' }}
                     onClick={handleDeleteArea}
                   >
                     🗑️ Delete
                   </button>
                 </div>
               ) : (
                 <button 
                   className="btn btn-primary" 
                   style={{ marginTop: '1rem', width: '100%', opacity: selectedArea ? 1 : 0.5 }}
                   onClick={handleSaveArea}
                   disabled={!selectedArea}
                 >
                   {selectedArea ? 'Save New Area' : 'Select an area first'}
                 </button>
               )}
               
               {selectedArea && (
                 <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px' }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#ef4444' }}>Bulk Delete Points</h4>
                    <p style={{ fontSize: '0.8rem', color: '#7f1d1d', margin: '0 0 1rem 0' }}>
                      Have too many points? Draw a rectangle to delete them all at once.
                    </p>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                      <input 
                        type="checkbox" 
                        checked={eraserActive}
                        onChange={(e) => setEraserActive(e.target.checked)}
                      />
                      Enable Eraser Rectangle
                    </label>
                    {eraserActive && (
                      <button 
                        className="btn" 
                        style={{ width: '100%', backgroundColor: '#ef4444', color: 'white' }}
                        onClick={handleDeleteEraserPoints}
                      >
                        ✂️ Delete Points in Rectangle
                      </button>
                    )}
                 </div>
               )}
             </div>
             </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3>Manage Teams</h3>
          <p style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: 'var(--color-text-light)' }}>
            Create new sales territories or update their names and colors.
          </p>

          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <input 
              type="text" 
              className="input-field" 
              placeholder="New Team Name (e.g. KA5)" 
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              style={{ flex: 1, maxWidth: '300px' }}
            />
            <button className="btn btn-primary" onClick={handleCreateTeam}>Add Team</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
            {teams.map(team => (
               <div key={team.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', border: '1px solid var(--color-border)', borderRadius: '8px' }}>
                 <input 
                   type="text" 
                   value={teamNames[team.id] || ''}
                   onChange={(e) => setTeamNames(prev => ({...prev, [team.id]: e.target.value}))}
                   className="input-field"
                   style={{ flex: 1, marginRight: '1rem', padding: '0.25rem 0.5rem', minWidth: '80px' }}
                 />
                 <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                   <input 
                     type="color" 
                     value={teamColors[team.id] || '#eb0a1e'} 
                     onChange={(e) => setTeamColors(prev => ({...prev, [team.id]: e.target.value}))}
                     style={{ cursor: 'pointer', padding: 0, border: 'none', width: '30px', height: '30px', borderRadius: '4px' }}
                   />
                   <button 
                     className="btn" 
                     style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem', backgroundColor: 'var(--color-primary)', color: 'white' }}
                     onClick={() => handleUpdateTeam(team.id)}
                   >
                     Save
                   </button>
                 </div>
               </div>
            ))}
          </div>
        </div>
      )}
      
      {activeTab === 'users' && (
        <div className="card">
          <h3>Manage Users</h3>
          <p style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: 'var(--color-text-light)' }}>
            Add Google accounts and assign them to roles and teams. User must login with `@tmhwst.toyota-industries.com`.
          </p>
          
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
            <input 
              type="text" 
              className="input-field" 
              placeholder="Email Prefix (e.g. john.d)" 
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              style={{ flex: 1, minWidth: '200px' }}
            />
              <select 
              className="input-field" 
              value={newUserRole} 
              onChange={(e) => setNewUserRole(e.target.value)}
            >
              <option value="sale">Sale</option>
              <option value="sale_manager">Sale Manager</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
            {['sale', 'sale_manager'].includes(newUserRole) && (
              <select 
                className="input-field" 
                value={newUserTeam} 
                onChange={(e) => setNewUserTeam(e.target.value)}
              >
                <option value="">Select Team</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.team_name}</option>)}
              </select>
            )}
            <input
              type="text"
              className="input-field"
              placeholder="Sales Code (e.g. BK)"
              value={newUserSalesCode}
              onChange={(e) => setNewUserSalesCode(e.target.value)}
              style={{ width: '150px' }}
            />
            <button className="btn btn-primary" onClick={handleCreateUser}>Add User</button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                  <th style={{ padding: '0.75rem' }}>Google Email</th>
                  <th style={{ padding: '0.75rem' }}>Role</th>
                  <th style={{ padding: '0.75rem' }}>Assigned Team</th>
                  <th style={{ padding: '0.75rem' }}>Sales Code</th>
                  <th style={{ padding: '0.75rem' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {usersList.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.75rem' }}>
                      <input
                        type="email"
                        className="input-field"
                        value={u.google_email || ''}
                        placeholder="email@tmhwst.toyota-industries.com"
                        onChange={(e) => setUsersList(prev => prev.map(user => user.id === u.id ? { ...user, google_email: e.target.value } : user))}
                        style={{
                          padding: '0.35rem 0.5rem',
                          fontSize: '0.9rem',
                          width: '100%',
                          minWidth: '220px',
                          border: !u.google_email ? '1.5px solid #ef4444' : undefined,
                          backgroundColor: !u.google_email ? '#fef2f2' : undefined
                        }}
                      />
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <select 
                        className="input-field" 
                        value={u.role}
                        onChange={(e) => setUsersList(prev => prev.map(user => user.id === u.id ? { ...user, role: e.target.value } : user))}
                        style={{ padding: '0.25rem', fontSize: '0.9rem' }}
                      >
                        <option value="sale">Sale</option>
                        <option value="sale_manager">Sale Manager</option>
                        <option value="admin">Admin</option>
                        <option value="super_admin">Super Admin</option>
                      </select>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {['sale', 'sale_manager'].includes(u.role) ? (
                        <select 
                          className="input-field" 
                          value={u.team_id || ''}
                          onChange={(e) => setUsersList(prev => prev.map(user => user.id === u.id ? { ...user, team_id: e.target.value } : user))}
                          style={{ padding: '0.25rem', fontSize: '0.9rem' }}
                        >
                          <option value="">No Team</option>
                          {teams.map(t => <option key={t.id} value={t.id}>{t.team_name}</option>)}
                        </select>
                      ) : (
                        <span style={{ color: 'var(--color-text-light)', fontSize: '0.9rem' }}>All Teams</span>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <input
                        type="text"
                        className="input-field"
                        value={u.sales_code || ''}
                        placeholder="Code"
                        onChange={(e) => setUsersList(prev => prev.map(user => user.id === u.id ? { ...user, sales_code: e.target.value } : user))}
                        style={{ padding: '0.25rem', fontSize: '0.9rem', width: '80px' }}
                      />
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        {currentUser?.google_email === u.google_email ? (
                          <span style={{ fontSize: '0.75rem', color: '#64748b', fontStyle: 'italic', padding: '0.25rem 0.5rem', backgroundColor: '#f1f5f9', borderRadius: '4px' }}>
                            (บัญชีคุณ)
                          </span>
                        ) : (
                          <button
                            className="btn"
                            style={{
                              padding: '0.25rem 0.6rem',
                              fontSize: '0.8rem',
                              backgroundColor: '#ea580c',
                              color: 'white',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.35rem',
                              fontWeight: 600,
                              borderRadius: '4px',
                              border: 'none',
                              boxShadow: '0 1px 2px rgba(234, 88, 12, 0.2)'
                            }}
                            onClick={() => handleImpersonateUser(u)}
                            title={`จำลองมุมมองเสมือนเข้าสู่ระบบด้วย ${u.google_email}`}
                          >
                            <Eye size={13} />
                            จำลองมุมมอง
                          </button>
                        )}
                        <button className="btn" style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem', backgroundColor: 'var(--color-primary)', color: 'white' }} onClick={() => handleUpdateUser(u)}>Save</button>
                        <button className="btn" style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem', backgroundColor: '#ef4444', color: 'white' }} onClick={() => handleDeleteUser(u.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {usersList.length === 0 && (
                  <tr>
                    <td colSpan="4" style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-text-light)' }}>No users found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'customers' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        <div className="card">
          <h3>Import Customer Data</h3>
          <p style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: 'var(--color-text-light)' }}>
            Copy your data from Excel (including the header row) and paste it into the box below. 
            The system will automatically detect columns based on header names (e.g. <strong>CD No., Customer Group, Search Name, Name, Sales: Team, Sales: Code, After-Sales: Code, Address, city</strong>). The order does not matter.
          </p>
          
          <textarea 
            id="excel-paste-box"
            className="input-field" 
            placeholder="Click here and press Ctrl+V / Cmd+V to paste Excel data..."
            style={{ width: '100%', height: '150px', fontFamily: 'monospace', marginBottom: '1rem', whiteSpace: 'pre' }}
            onChange={handlePasteExcel}
          />

          {customerData.length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4>Preview Data ({customerData.length} rows ready)</h4>
                <button className="btn btn-primary" onClick={handleSaveCustomers}>
                  Update to Database
                </button>
              </div>
              <div style={{ overflowX: 'auto', maxHeight: '400px', border: '1px solid var(--color-border)', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f1f5f9', zIndex: 1, userSelect: 'none' }}>
                    <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                      {['CD_No', 'Customer_Group', 'Search_Name', 'Customer_Name', 'Sales_Team', 'Update_Sales_Code', 'Update_After_Sales_Code', 'Address', 'City'].map((col) => (
                        <th 
                          key={col}
                          style={{ padding: '0.5rem', cursor: 'pointer' }}
                          onClick={() => handleSort(col, importSortConfig, setImportSortConfig)}
                        >
                          {col} {importSortConfig.key === col ? (importSortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedImportData.slice(0, 50).map((row, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '0.5rem' }}>{row.CD_No}</td>
                        <td style={{ padding: '0.5rem' }}>{row.Customer_Group}</td>
                        <td style={{ padding: '0.5rem' }}>{row.Search_Name}</td>
                        <td style={{ padding: '0.5rem' }}>{row.Customer_Name}</td>
                        <td style={{ padding: '0.5rem', color: 'var(--color-primary)', fontWeight: 600 }}>{row.Sales_Team}</td>
                        <td style={{ padding: '0.5rem' }}>{row.Update_Sales_Code}</td>
                        <td style={{ padding: '0.5rem' }}>{row.Update_After_Sales_Code}</td>
                        <td style={{ padding: '0.5rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.Address}</td>
                        <td style={{ padding: '0.5rem' }}>{row.City}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {customerData.length > 50 && <div style={{ padding: '0.5rem', textAlign: 'center', color: 'var(--color-text-light)' }}>Showing first 50 rows...</div>}
              </div>
            </div>
          )}

          <div style={{ marginTop: '3rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Existing Database Customers ({filteredCustomers.length} / {existingCustomers.length})</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn" style={{ backgroundColor: '#ef4444', color: 'white', borderColor: '#dc2626' }} onClick={handleDeleteAllCustomers}>
                  🗑️ Delete All Data
                </button>
                <button className="btn" style={{ backgroundColor: '#10b981', color: 'white', borderColor: '#059669' }} onClick={handleExportExcel}>
                  📥 Export to Excel
                </button>
                <button className="btn btn-primary" onClick={handleStartAutoGeocode}>
                  📍 Auto-Pin Missing Locations
                </button>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                type="text"
                className="input-field"
                placeholder="Search CD_No, Name, Code, City, Group..."
                value={customerSearchQuery}
                onChange={(e) => setCustomerSearchQuery(e.target.value)}
                style={{ flex: 1, minWidth: '250px' }}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem', color: 'var(--color-text)' }}>
                <input
                  type="checkbox"
                  checked={showFailedGeocodeOnly}
                  onChange={(e) => setShowFailedGeocodeOnly(e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                Show Only Missing Locations (Failed / No Pin)
              </label>
            </div>
            
            <APIProvider apiKey={API_KEY}>
              <GeocodeHelper customersToGeocode={pendingGeocodes} onGeocodeComplete={handleGeocodeComplete} />
            </APIProvider>

            <div style={{ overflowX: 'auto', maxHeight: '500px', border: '1px solid var(--color-border)', borderRadius: '8px', marginTop: '1rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f1f5f9', zIndex: 1, userSelect: 'none' }}>
                  <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                    {[
                      { label: 'CD_No', key: 'cd_no' },
                      { label: 'Group', key: 'customer_group' },
                      { label: 'Search Name', key: 'search_name' },
                      { label: 'Customer Name', key: 'customer_name' },
                      { label: 'Sales Team', key: 'sales_team' },
                      { label: 'Sales Code', key: 'update_sales_code' },
                      { label: 'After Sales Code', key: 'update_after_sales_code' },
                      { label: 'Address', key: 'address' },
                      { label: 'City', key: 'city' },
                      { label: 'Pin Status', key: 'geocode_status' }
                    ].map(col => (
                      <th 
                        key={col.key}
                        style={{ padding: '0.5rem', cursor: 'pointer' }}
                        onClick={() => handleSort(col.key, customerSortConfig, setCustomerSortConfig)}
                      >
                        {col.label} {customerSortConfig.key === col.key ? (customerSortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                      </th>
                    ))}
                    <th style={{ padding: '0.5rem' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFilteredCustomers.slice(0, 100).map((c, idx) => {
                    const isEditing = editingCustomerCdNo === c.cd_no;
                    return (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '0.5rem' }}>{c.cd_no}</td>
                      <td style={{ padding: '0.5rem' }}>
                        {isEditing ? (
                          <input type="text" className="input-field" style={{ padding: '0.2rem', fontSize: '0.8rem', minWidth: '100px' }} value={editCustomerData.customer_group} onChange={e => setEditCustomerData({...editCustomerData, customer_group: e.target.value})} />
                        ) : c.customer_group}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        {isEditing ? (
                          <input type="text" className="input-field" style={{ padding: '0.2rem', fontSize: '0.8rem', minWidth: '150px' }} value={editCustomerData.search_name} onChange={e => setEditCustomerData({...editCustomerData, search_name: e.target.value})} />
                        ) : c.search_name}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        {isEditing ? (
                          <input type="text" className="input-field" style={{ padding: '0.2rem', fontSize: '0.8rem', minWidth: '150px' }} value={editCustomerData.customer_name || ''} onChange={e => setEditCustomerData({...editCustomerData, customer_name: e.target.value})} />
                        ) : c.customer_name}
                      </td>
                      <td style={{ padding: '0.5rem', color: 'var(--color-primary)', fontWeight: 600 }}>
                        {isEditing ? (
                          <input type="text" className="input-field" style={{ padding: '0.2rem', fontSize: '0.8rem', minWidth: '80px' }} value={editCustomerData.sales_team || ''} onChange={e => setEditCustomerData({...editCustomerData, sales_team: e.target.value})} />
                        ) : c.sales_team}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        {isEditing ? (
                          <input type="text" className="input-field" style={{ padding: '0.2rem', fontSize: '0.8rem', minWidth: '80px' }} value={editCustomerData.update_sales_code || ''} onChange={e => setEditCustomerData({...editCustomerData, update_sales_code: e.target.value})} />
                        ) : c.update_sales_code}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        {isEditing ? (
                          <input type="text" className="input-field" style={{ padding: '0.2rem', fontSize: '0.8rem', minWidth: '80px' }} value={editCustomerData.update_after_sales_code || ''} onChange={e => setEditCustomerData({...editCustomerData, update_after_sales_code: e.target.value})} />
                        ) : c.update_after_sales_code}
                      </td>
                      <td style={{ padding: '0.5rem', maxWidth: isEditing ? 'none' : '200px', overflow: isEditing ? 'visible' : 'hidden', textOverflow: isEditing ? 'clip' : 'ellipsis' }}>
                        {isEditing ? (
                          <input type="text" className="input-field" style={{ padding: '0.2rem', fontSize: '0.8rem', width: '100%', minWidth: '200px' }} value={editCustomerData.address} onChange={e => setEditCustomerData({...editCustomerData, address: e.target.value})} />
                        ) : c.address}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        {isEditing ? (
                          <input type="text" className="input-field" style={{ padding: '0.2rem', fontSize: '0.8rem', minWidth: '100px' }} value={editCustomerData.city} onChange={e => setEditCustomerData({...editCustomerData, city: e.target.value})} />
                        ) : c.city}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        {c.lat && c.lng ? (
                          <span style={{ color: '#16a34a', fontWeight: 600 }}>🟢 {c.geocode_status === 'manual' ? 'Manual' : 'Pinned'}</span>
                        ) : c.geocode_status === 'failed' ? (
                          <span style={{ color: '#dc2626', fontWeight: 600 }}>🔴 Failed</span>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>⚪ Pending</span>
                        )}
                      </td>
                      <td style={{ padding: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        {isEditing ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button className="btn btn-primary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }} onClick={handleSaveEdit}>Save</button>
                              <button className="btn" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db' }} onClick={handleCancelEdit}>Cancel</button>
                            </div>
                            <label style={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              <input type="checkbox" checked={editCustomerData.reset_location || false} onChange={e => setEditCustomerData({...editCustomerData, reset_location: e.target.checked})} style={{ margin: 0 }} />
                              Reset Pin
                            </label>
                          </div>
                        ) : (
                          <>
                            <button className="btn" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', backgroundColor: '#e0e7ff', color: '#3730a3', border: '1px solid #c7d2fe' }} onClick={() => handleEditCustomerClick(c)}>Edit</button>
                            <button className="btn" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', backgroundColor: '#f3f4f6', color: '#1f2937', border: '1px solid #d1d5db' }} onClick={() => { 
                              setManualGeocodeCustomer(c); 
                              setManualPinLocation(c.lat && c.lng && c.geocode_status !== 'failed' ? { lat: parseFloat(c.lat), lng: parseFloat(c.lng) } : null);
                              setActiveTab('areas'); 
                            }}>Fix Location</button>
                          </>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                  {existingCustomers.length === 0 && (
                    <tr><td colSpan="8" style={{ padding: '1rem', textAlign: 'center' }}>No customers in database.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        <div className="card" style={{ marginTop: '2rem', border: '1px solid #fca5a5' }}>
          <h3 style={{ color: '#ef4444' }}>Marketing Leads Database</h3>
          <p style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: 'var(--color-text-light)' }}>
            You can clear the marketing leads from the local database without affecting Google Sheets.
          </p>
          <button className="btn" style={{ backgroundColor: '#ef4444', color: 'white', borderColor: '#dc2626' }} onClick={handleDeleteAllLeads}>
            🗑️ Delete All Local Leads Data
          </button>
        </div>
        </div>
      )}

      {/* Team Emails Tab */}
      {activeTab === 'teamemails' && (
        <div className="card">
          <h3 style={{ marginBottom: '0.5rem' }}>📧 Team Email Notifications</h3>
          <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '1.5rem' }}>
            Set the email address(es) for each sales team. When a new lead is assigned to a team, an email will be sent automatically.
            Multiple emails can be separated by commas (e.g. <code>team1@example.com, team2@example.com</code>).
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {teams.map(team => (
              <div key={team.id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                
                {/* Team Color Badge */}
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: teamColors[team.id] || '#eb0a1e', flexShrink: 0 }}></div>
                
                {/* Team Name */}
                <div style={{ minWidth: '120px', fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
                  {teamNames[team.id] || team.team_name}
                </div>

                {/* Email Input */}
                <input
                  type="text"
                  className="input-field"
                  placeholder="team@company.com, manager@company.com"
                  value={teamEmails[team.id] || ''}
                  onChange={(e) => setTeamEmails(prev => ({ ...prev, [team.id]: e.target.value }))}
                  style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                />

                {/* Save Button */}
                <button
                  className="btn btn-primary"
                  onClick={() => handleSaveTeamEmail(team.id)}
                  style={{ padding: '0.5rem 1.25rem', whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  Save
                </button>
              </div>
            ))}
          </div>

          {teams.length === 0 && (
            <p style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>No teams found. Please create a team first.</p>
          )}
        </div>
      )}
      {activeTab === 'products' && (
        <div style={{ flex: 1, minHeight: 0 }}>
          <ProductSettings />
        </div>
      )}
      {activeTab === 'branding' && (
        <div style={{ flex: 1, minHeight: 0 }}>
          <CompanyBrandingSettings />
        </div>
      )}
      {activeTab === 'models3d' && (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <Model3DManager />
        </div>
      )}
      {activeTab === 'email_settings' && (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <EmailSettings onNavigateTab={(tab) => setActiveTab(tab)} />
        </div>
      )}

    </div>
  );
}
